import {TreeModel} from "@components/tree/tree.model";
import {ToolDocument} from "@app/tool/model/toolDocument.model";
import {Tool} from "@app/tool/model/tool.model";
import {ToolSet} from "@app/tool/model/toolSet.model";
import {Injectable} from "@angular/core";
import {ISaveTicket} from "@models/interfaces/save.ticket.interface";

import * as moment from 'moment';
import * as _ from "lodash";
import {ToolDocumentTreeNode} from "@app/tool/model/tool-document-tree-node.model";


@Injectable()
export class TreeAdapter {

    constructor() {
    }

    adaptEquipmentToTree(items, displayField) {
        if (!items) {
            return [];
        }

        return items.map((item) => {
            const data = new TreeModel();
            data.label = item[displayField] + ":" + item.line[displayField];
            data.collapsedIcon = "fa fa-cog";
            data.expandedIcon = "fa fa-cog";
            data.expanded = false;
            data.children = item.processTypes.map((processType) => {
                const lineModel = new TreeModel();
                lineModel.label = processType[displayField];
                lineModel.expandedIcon = 'fa fa-dot-circle-o';
                lineModel.collapsedIcon = 'fa fa-dot-circle-o';
                return lineModel
            });

            return data;
        });
    }


    adaptToolsToTree(items, type = 'tree-model', withStance = false) {
        if (!items || items.length === 0) {
            return items;
        }
        const sets = [];
        let individualTools = [];

        items.map((item: any) => {
            if (item instanceof Tool) {
                individualTools = individualTools.concat(this.mapTool(item, type, withStance));
            } else if (item instanceof ToolSet) {
                sets.push(this.mapSet(item, type, withStance));
            }
        });

        return sets.concat(individualTools);
    }

    adaptToolsForLocationTree(items, type) {
        if (items === undefined || items.length === 0) {
            return items;
        }

        const toolLocations = [];
        items.map((item: any) => {
            if (item instanceof Tool) {
                const individualTool = this.mapTool(item, type);
                if (!individualTool.data.positionLabel) {
                    toolLocations.push(individualTool)
                } else {
                    const toolLocation = toolLocations.find(x => x.label === individualTool.data.positionLabel);
                    if (toolLocation) {
                        toolLocation.children.push(individualTool)
                    } else {
                        const treeModelLocation = this.mapToolLocation(individualTool.data.positionLabel, individualTool)
                        toolLocations.push(treeModelLocation);
                    }
                }
            } else if (item instanceof ToolSet) {
                const groupedTools = _.toArray(_.groupBy(item.tools, 'positionLabel'));
                const allToolsFromSet = item.tools;

                groupedTools.forEach(tool => {
                    const locationName = tool[0].positionLabel;
                    const toolIds = tool.map(x => x.id);

                    item.tools = allToolsFromSet.filter(x => toolIds.includes(x.id));
                    if (!locationName) {
                        toolLocations.push(this.mapSet(item, type));
                    } else {
                        const toolLocation = toolLocations.find(x => x.label === locationName);
                        if (toolLocation) {
                            toolLocation.children.push(this.mapSet(item, type))
                        } else {
                            const treeModelLocation = this.mapToolLocation(locationName, this.mapSet(item, type));
                            toolLocations.push(treeModelLocation);
                        }
                    }
                });
            }
        });

        return toolLocations;
    }


    /**
     * @MR Hackerman 101 to non-recursivity pitfall
     * @param items
     * @param displayField
     */
    adaptDocumentsToTree(items, displayField) {
        if (!items) {
            return []
        }

        return items.map((item: ToolDocument) => {
            const data = new TreeModel();
            data.label = item[displayField];
            data.collapsedIcon = this.computeFolderIcon(item);
            data.expandedIcon = this.computeFolderIcon(item);
            data.isTopLevel = true;
            data.data = item;
            data.children = this.processTree(item.childPaths, displayField, data, item.isMandatory) || [];
            if (item.items !== null && item.items.length > 0) {
                data.children = item.items.length > 0
                    ? data.children.concat(this.processFilesForTree(item.items, data)) : data.children;
            }
            return data;
        });
    }

    adaptDocumentLocationsToTree(items, displayField) {
        if (!items) {
            return []
        }

        return items.map((item: ToolDocumentTreeNode) => {
            const data = new TreeModel();
            data.label = item[displayField];
            data.collapsedIcon = this.isFile(item.fullPath) ? this.computeFileIcon(item.name) : this.computeFolderIcon(item.name);
            data.expandedIcon = this.isFile(item.fullPath) ? this.computeFileIcon(item.name) : this.computeFolderIcon(item.name);
            data.isTopLevel = true;
            data.data = item;
            data.fullPath = item.fullPath;
            data.isFile = item.isFile;
            data.isSelected = item.isSelected;
            data.expanded = item.isSelected || this.shouldBePartiallySelected(item.childNodes);
            data.partialSelected = item.isSelected ? false : this.shouldBePartiallySelected(item.childNodes);
            data.children = this.processTree(item.childNodes, displayField, data, false, true) || [];
            if (item.items !== null && item.items.length > 0) {
                data.children = item.items.length > 0
                    ? data.children.concat(this.processFilesForDocumentLocations(item.items, data)) : data.children;
            }
            return data;
        });
    }

    processTree(treeNodes: ToolDocumentTreeNode[], displayField: string, parent: TreeModel, isParentMandatory: boolean = false,
                isSelectable: boolean = false): TreeModel[] {
        /* tslint:disable */
        let result: TreeModel[] = [];
        /* tslint:enable */
        if (treeNodes === null || treeNodes.length === 0) {
            return;
        }
        for (const treeNode of treeNodes) {
            const data = new TreeModel();
            data.label = treeNode.name;
            data.expandedIcon = isParentMandatory ? 'fa fa-folder' : 'fa fa-folder-o';
            data.collapsedIcon = isParentMandatory ? 'fa fa-folder' : 'fa fa-folder-o';
            data.leaf = treeNode.childNodes.length === 0;
            data.data = treeNode.items;
            data.parent = parent;
            data.expanded = false;
            data.selectable = isSelectable;
            data.isSubfolder = true;
            data.isFile = treeNode.isFile;
            data.fullPath = treeNode.fullPath;
            data.isSelected = treeNode.isSelected;
            data.expanded = treeNode.isSelected || this.shouldBePartiallySelected(treeNode.childNodes);
            data.partialSelected = treeNode.isSelected ? false : this.shouldBePartiallySelected(treeNode.childNodes)
            if (treeNode.items.length > 0) {
                data.children = this.processFilesForDocumentLocations(treeNode.items, parent);
            }
            data.children = treeNode.childNodes.length > 0
                ? data.children.concat(this.processTree(treeNode.childNodes, displayField, data, isParentMandatory, isSelectable))
                : data.children;
            result.push(data);
            if (treeNode.childNodes.length > 0) {
                this.processTree(treeNode.childNodes, displayField, data, isParentMandatory, isSelectable);
            }
        }
        return result;
    }

    private processFilesForDocumentLocations(files: ToolDocumentTreeNode[], parent) {
        return files.map((file: ToolDocumentTreeNode) => {
            const data = new TreeModel();
            data.label = this.computeFileNameFromPath(file.fullPath);
            data.collapsedIcon = this.isFile(file.fullPath) ? this.computeFileIcon(file.fullPath) : this.computeFolderIcon(file.fullPath);
            data.expandedIcon = this.isFile(file.fullPath) ? this.computeFileIcon(file.fullPath) : this.computeFolderIcon(file.fullPath);
            data.data = file;
            data.parent = parent;
            data.leaf = true;
            data.isFile = true;
            data.fullPath = file.fullPath;
            data.isSelected = file.isSelected;
            return data;
        });
    }

    private processFilesForTree(files: ToolDocumentTreeNode[], parent) {
        return files.map((file: ToolDocumentTreeNode) => {
            const data = new TreeModel();
            data.label = file.name;
            data.collapsedIcon = this.isFile(file.fullPath) ? this.computeFileIcon(file.fullPath)
                : this.computeFolderIcon(file.fullPath);
            data.expandedIcon = this.isFile(file.fullPath) ? this.computeFileIcon(file.fullPath)
                : this.computeFolderIcon(file.fullPath);
            data.data = file;
            data.parent = parent;
            data.leaf = true;
            data.isFile = true;
            data.fullPath = file.fullPath;
            data.isSelected = file.isSelected;
            return data;
        });
    }

    shouldBePartiallySelected(nodes: ToolDocumentTreeNode[]) {
        if (nodes.length === 0 || nodes === undefined) {
            return;
        }
        if (nodes.some(x => x.isSelected)) {
            return true
        }
        for (const treeNode of nodes) {
            const recursiveResult = this.shouldBePartiallySelected(treeNode.childNodes);
            if (recursiveResult) {
                return recursiveResult;
            }
        }
    }

    adaptTicketOverviewToTree(items, displayField) {
        if (!items) {
            return []
        }

        return items.map((item: ISaveTicket) => {
            const data = new TreeModel();
            data.label = 'TicketId: ' + item[displayField];
            data.isTopLevel = true;
            data.data = {
                date: moment(item.ticketOpenDate).format('DD/MM/YYYY - HH:mm'),
                tools: item.toolNames.join('<br>&emsp;&emsp;&emsp;'),
                ticketStatus: item.ticketStatus,
                operations: item.ticketOperations.join(', '),
                description: item.ticketDescription ? item.ticketDescription : '',
            }
            return data;
        })
    }

    private mapToolLocation(label, children) {
        const treeModel = new TreeModel();
        treeModel.label = label;
        treeModel.children.push(children);
        treeModel.expanded = false;
        treeModel.data = treeModel.children;
        return treeModel;
    }

    private mapSet(set, type, withStance = false) {
        const setOfTools = new TreeModel();
        setOfTools.label = `${set.name} - SET`;
        setOfTools.data = set;
        setOfTools.icon = 'fa fa-cogs';
        setOfTools.selectable = type !== "tool-inspect";
        setOfTools.expanded = false;
        setOfTools.inspected = true;
        setOfTools.children = setOfTools.children.concat(this.mapTools(set.tools, type, withStance));
        return setOfTools;
    }

    private mapTools(tools: Tool[], type: string, withStance: boolean) {
        return tools.map(tool => {
            return this.mapTool(tool, type, withStance);
        });
    }

    private mapTool(tool: Tool, type: string, withStance = false) {
        return new TreeModel({
            label: this.buildLabel(tool, withStance),
            icon: 'fa fa-cog',
            data: tool,
            type: type,
            warning: tool.issue && tool.issue.id,
            inspected: tool.isScanned,
            selectable: type !== "tool-inspect",
            leaf: true
        });
    }

    private computeFolderIcon(item) {
        return item.isMandatory ? 'fa fa-folder' : 'fa fa-folder-o';
    }

    private computeFileIcon(item: string) {
        const extension = '.' + item.split('.').pop();
        switch (extension) {
            case '.pdf':
                return 'fa fa-file-pdf-o';
            case '.doc':
            case '.docx':
                return 'fa fa-file-text';
            case '.txt':
                return 'fa fa-file-text-o';
            default:
                return 'fa fa-file-text-o';
        }
    }

    private buildLabel(tool, withStance: boolean) {
        let label = tool.idFromSet > 0
            ? `${tool.name} - ${tool.toolUid} - ${tool.idFromSet}`
            : `${tool.name} - ${tool.toolUid}`;
        if (withStance) {
            label += tool.stance ? ` - ${tool.status}(${tool.stance})` : ` - ${tool.status}`;
        }

        return label;
    }

    private computeFileNameFromPath(item: string) {
        return item.split('\\').pop().split('/').pop();
    }

    private isFile(path: string): boolean {
        return path.split('\\').pop().split('/').pop().indexOf('.') > 0;
    }
}
